import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Printer,
  Download,
  CheckCircle,
  XCircle,
  Users,
  Edit,
  Package,
  CreditCard,
  Wallet,
  Upload,
  File,
  Image,
  X,
  Loader2,
  Eye,
  MessageSquare
} from 'lucide-react';
import { useInstallment, useUpdateInstallment, useGenerateInstallmentPDF } from '../../hooks/useInstallments';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../api/clients';

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    activo: { variant: 'info', label: 'Activo' },
    moroso: { variant: 'danger', label: 'Moroso' },
    completado: { variant: 'success', label: 'Completado' },
    cancelado: { variant: 'default', label: 'Cancelado' },
  };
  const config = variants[estado] || variants.activo;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getEstadoPagoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pagada: { variant: 'success', label: 'Pagada' },
    pendiente: { variant: 'warning', label: 'Pendiente' },
    vencida: { variant: 'danger', label: 'Vencida' },
    morosa: { variant: 'danger', label: 'Morosa' },
    cancelada: { variant: 'default', label: 'Cancelada' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

export function InstallmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const installmentId = id ? parseInt(id) : 0;

  const { data: contrato, isLoading, refetch } = useInstallment(installmentId);
  const updateInstallment = useUpdateInstallment();
  const generatePDF = useGenerateInstallmentPDF();

  const [modalPago, setModalPago] = useState(false);
  const [modalDetallePago, setModalDetallePago] = useState(false);
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState<any>(null);
  const [pagoSeleccionado, setPagoSeleccionado] = useState<any>(null);
  const [montoPagado, setMontoPagado] = useState(0);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [observacionesPago, setObservacionesPago] = useState('');
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().split('T')[0]);
  const [horaPago, setHoraPago] = useState(new Date().toTimeString().slice(0, 5));
  const [isSubmittingPago, setIsSubmittingPago] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [imagenPago, setImagenPago] = useState<string | null>(null);
  const [pdfPago, setPdfPago] = useState<string | null>(null);
  const [selectedFilesPago, setSelectedFilesPago] = useState<File[]>([]);
  const [uploadingPago, setUploadingPago] = useState(false);
  const [uploadProgressPago, setUploadProgressPago] = useState(0);

  const handleChangeEstado = async (nuevoEstado: string) => {
    const mensajeConfirmacion = nuevoEstado === 'completado' 
      ? ' ¿Marcar TODAS las cuotas como pagadas y completar el contrato?\n\nEsta acción no se puede deshacer.' 
      : nuevoEstado === 'cancelado'
      ? ' ¿Cancelar el contrato y TODAS sus cuotas?\n\nEsta acción no se puede deshacer.'
      : `¿Cambiar estado a "${nuevoEstado}"?`;

    if (!confirm(mensajeConfirmacion)) return;

    try {
      await updateInstallment.mutateAsync({
        id: installmentId,
        data: { estado: nuevoEstado },
      });
      
      if (nuevoEstado === 'completado') {
        toast.success(' ¡Contrato completado! Todas las cuotas fueron marcadas como pagadas.');
      } else if (nuevoEstado === 'cancelado') {
        toast.success(' Contrato cancelado. Todas las cuotas fueron canceladas.');
      }
      
      await refetch();
      setTimeout(async () => {
        await refetch();
      }, 500);
      
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cambiar el estado del contrato');
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const result = await generatePDF.mutateAsync(installmentId);
      if (result.pdfUrl) {
        window.open(result.pdfUrl, '_blank');
      }
    } catch (error) {
      // Error ya manejado en el hook
    }
  };

  const uploadFilesPago = async (files: File[]): Promise<{ imagen: string | null; pdf: string | null }> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          setUploadProgressPago(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      },
    });

    const result = response.data.data;
    return {
      imagen: result.imagenes?.length > 0 ? result.imagenes[0] : null,
      pdf: result.pdfUrl || null,
    };
  };

  const handleFileSelectPago = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const totalFiles = selectedFilesPago.length + files.length;
    
    if (totalFiles > 2) {
      toast.error('Máximo 2 archivos (1 imagen + 1 PDF)');
      return;
    }

    const imagenesCount = selectedFilesPago.filter(f => f.type.startsWith('image/')).length + 
      files.filter(f => f.type.startsWith('image/')).length;
    const pdfCount = selectedFilesPago.filter(f => f.type === 'application/pdf').length + 
      files.filter(f => f.type === 'application/pdf').length;

    if (imagenesCount > 1) {
      toast.error('Máximo 1 imagen');
      return;
    }
    if (pdfCount > 1) {
      toast.error('Máximo 1 PDF');
      return;
    }

    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error(`"${file.name}" no es un formato válido`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" supera el límite de 10MB`);
        return false;
      }
      return true;
    });

    setSelectedFilesPago(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeSelectedFilePago = (index: number) => {
    setSelectedFilesPago(prev => prev.filter((_, i) => i !== index));
  };

  const removeImagenPago = () => {
    setImagenPago(null);
  };

  const removePdfPago = () => {
    setPdfPago(null);
  };

  const handlePagarCuota = async () => {
    setIsSubmittingPago(true);
    setUploadingPago(true);
    setUploadProgressPago(0);

    try {
      const fechaHoraPago = new Date(`${fechaPago}T${horaPago}:00`);
      
      let imagenUrl = imagenPago;
      let pdfUrl = pdfPago;
      
      if (selectedFilesPago.length > 0) {
        const result = await uploadFilesPago(selectedFilesPago);
        imagenUrl = result.imagen || imagenUrl;
        pdfUrl = result.pdf || pdfUrl;
      }
      
      await api.post(`/installments/cuota/${cuotaSeleccionada.id}/pagar`, {
        montoPagado,
        metodoPago,
        observaciones: observacionesPago,
        fechaPago: fechaHoraPago.toISOString(),
        imagenUrl,
        pdfUrl,
      });
      
      toast.success(` Cuota #${cuotaSeleccionada.numeroCuota} pagada exitosamente`);
      setModalPago(false);
      setImagenPago(null);
      setPdfPago(null);
      setSelectedFilesPago([]);
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al pagar la cuota');
    } finally {
      setIsSubmittingPago(false);
      setUploadingPago(false);
      setUploadProgressPago(0);
    }
  };

  const handleVerDetallePago = (pago: any) => {
    setPagoSeleccionado(pago);
    setModalDetallePago(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando contrato...</div>
      </div>
    );
  }

  if (!contrato) {
    return (
      <div className="text-center py-8 text-red-500">
        Contrato no encontrado
      </div>
    );
  }

  const puedeEditar = contrato.estado === 'activo';
  const puedeCancelar = contrato.estado === 'activo';
  const puedeCompletar = contrato.estado === 'activo';

  const cuotasPagadas = contrato.pagos?.filter((p: any) => p.estado === 'pagada').length || 0;
  const cuotasPendientes = contrato.pagos?.filter((p: any) => p.estado === 'pendiente').length || 0;
  const cuotasVencidas = contrato.pagos?.filter((p: any) => p.estado === 'vencida' || p.estado === 'morosa').length || 0;

  const totalFilesPago = (imagenPago ? 1 : 0) + (pdfPago ? 1 : 0) + selectedFilesPago.length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/cuotas')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">
                {contrato.numeroContrato}
              </h1>
              {getEstadoBadge(contrato.estado)}
            </div>
            <p className="text-sm text-gray-500">
              {contrato.clienteNombre} • {formatDate(contrato.fechaCreacion)}
            </p>
          </div>
        </div>
      <div className="flex gap-2">
        {puedeEditar && (
          <Button
            variant="outline"
            onClick={() => navigate(`/cuotas/editar/${contrato.id}`)}
          >
            <Edit size={16} className="mr-2" />
            Editar
          </Button>
        )}
        {puedeCancelar && (
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => handleChangeEstado('cancelado')}
          >
            <XCircle size={16} className="mr-2" />
            Cancelar Contrato
          </Button>
        )}
        {puedeCompletar && (
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => handleChangeEstado('completado')}
          >
            <CheckCircle size={16} className="mr-2" />
            Completar Contrato
          </Button>
        )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cliente */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users size={20} className="text-blue-500" />
              Datos del Cliente
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium text-gray-800">{contrato.clienteNombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">DNI</p>
                <p className="font-medium text-gray-800">{contrato.clienteDni}</p>
              </div>
              {contrato.clienteTelefono && (
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="font-medium text-gray-800">{contrato.clienteTelefono}</p>
                </div>
              )}
              {contrato.clienteEmail && (
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-800">{contrato.clienteEmail}</p>
                </div>
              )}
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Dirección</p>
                <p className="font-medium text-gray-800">{contrato.clienteDireccion}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Localidad</p>
                <p className="font-medium text-gray-800">{contrato.clienteLocalidad}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Provincia</p>
                <p className="font-medium text-gray-800">{contrato.clienteProvincia}</p>
              </div>
            </div>
          </Card>

          {/* Producto */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={20} className="text-blue-500" />
              Producto
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Producto</p>
                <p className="font-medium text-gray-800">{contrato.productoNombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Precio</p>
                <p className="font-medium text-gray-800">{formatCurrency(contrato.productoPrecioTotal)}</p>
              </div>
              {contrato.productoDescripcion && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Descripción</p>
                  <p className="text-sm text-gray-600">{contrato.productoDescripcion}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Gestión de Cuotas */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CreditCard size={20} className="text-blue-500" />
              Gestión de Cuotas
            </h2>
            
            {/* Resumen */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{contrato.cantidadCuotas}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{cuotasPagadas}</p>
                <p className="text-xs text-gray-500">Pagadas</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{cuotasPendientes}</p>
                <p className="text-xs text-gray-500">Pendientes</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{cuotasVencidas}</p>
                <p className="text-xs text-gray-500">Vencidas</p>
              </div>
            </div>

            {/* Tabla de cuotas */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-2">N°</th>
                    <th className="pb-2">Vencimiento</th>
                    <th className="pb-2 text-right">Monto</th>
                    <th className="pb-2 text-center">Estado</th>
                    <th className="pb-2 text-center">Fecha Pago</th>
                    <th className="pb-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {contrato.pagos?.map((pago: any) => (
                    <tr key={pago.id} className="text-sm hover:bg-gray-50 transition-colors">
                      <td className="py-2 font-medium">{pago.numeroCuota}</td>
                      <td className="py-2">{formatDate(pago.fechaVencimiento)}</td>
                      <td className="py-2 text-right">{formatCurrency(pago.montoEsperado)}</td>
                      <td className="py-2 text-center">
                        {getEstadoPagoBadge(pago.estado)}
                      </td>
                      <td className="py-2 text-center">
                        {pago.fechaPago ? formatDate(pago.fechaPago) : '-'}
                      </td>
                      <td className="py-2 text-center">
                        {pago.estado === 'pendiente' && contrato.estado === 'activo' && (
                          <button
                            onClick={() => {
                              setCuotaSeleccionada(pago);
                              setMontoPagado(pago.montoEsperado);
                              setModalPago(true);
                            }}
                            className="px-3 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors"
                            title="Pagar cuota"
                          >
                            Pagar
                          </button>
                        )}
                        {pago.estado === 'pagada' && (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-xs text-gray-400">
                              {pago.metodoPago || 'Pagado'}
                            </span>
                            <button
                              onClick={() => handleVerDetallePago(pago)}
                              className="p-1 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                              title="Ver detalle del pago"
                            >
                              <Eye size={14} />
                            </button>
                          </div>
                        )}
                        {pago.estado === 'cancelada' && (
                          <span className="text-xs text-gray-400">Cancelada</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          {/* Resumen financiero */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Resumen Financiero</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Precio Producto</span>
                <span>{formatCurrency(contrato.productoPrecioTotal)}</span>
              </div>
              {contrato.intereses > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Intereses ({contrato.intereses}%)</span>
                  <span>{formatCurrency(contrato.totalConInteres - contrato.productoPrecioTotal - (contrato.costoEnvio || 0))}</span>
                </div>
              )}
              {contrato.costoEnvio > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Costo de Envío</span>
                  <span>{formatCurrency(contrato.costoEnvio)}</span>
                </div>
              )}
              {contrato.anticipoPorcentaje > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Anticipo ({contrato.anticipoPorcentaje}%)</span>
                  <span>-{formatCurrency(contrato.anticipoMonto)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold">
                  <span>Total a Financiar</span>
                  <span className="text-blue-600">{formatCurrency(contrato.saldoFinanciar)}</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Valor por Cuota</span>
                <span className="font-semibold">{formatCurrency(contrato.montoCuota)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Frecuencia</span>
                <span className="font-semibold capitalize">{contrato.frecuenciaPago}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fecha de Inicio</span>
                <span className="font-semibold">{formatDate(contrato.fechaInicio)}</span>
              </div>
            </div>
          </Card>

          {/* PDF */}
          {contrato.pdfUrl ? (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">PDF del Contrato</h2>
              <div className="flex gap-2">
                <a
                  href={contrato.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 text-blue-600 border border-blue-200 hover:bg-blue-50 py-2 rounded-lg transition-colors"
                >
                  <FileText size={16} />
                  Ver PDF
                </a>
                <button
                  onClick={() => window.open(contrato.pdfUrl, '_blank')}
                  className="flex-1 flex items-center justify-center gap-2 text-green-600 border border-green-200 hover:bg-green-50 py-2 rounded-lg transition-colors"
                >
                  <Download size={16} />
                  Descargar
                </button>
              </div>
            </Card>
          ) : (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">PDF del Contrato</h2>
              <Button
                onClick={handleGeneratePDF}
                className="w-full"
                variant="outline"
              >
                <Printer size={16} className="mr-2" />
                Generar PDF del Contrato
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* Modal de Pago */}
      <Modal
        isOpen={modalPago}
        onClose={() => {
          setModalPago(false);
          setImagenPago(null);
          setPdfPago(null);
          setSelectedFilesPago([]);
        }}
        title={`Pagar Cuota #${cuotaSeleccionada?.numeroCuota || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Información de la cuota */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Monto a pagar</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(cuotaSeleccionada?.montoEsperado || 0)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Vencimiento</p>
                <p className="text-sm font-medium text-gray-700">
                  {cuotaSeleccionada?.fechaVencimiento ? formatDate(cuotaSeleccionada.fechaVencimiento) : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Fecha y hora de pago */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago
              </label>
              <Input
                type="date"
                value={fechaPago}
                onChange={(e) => setFechaPago(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora de Pago
              </label>
              <Input
                type="time"
                value={horaPago}
                onChange={(e) => setHoraPago(e.target.value)}
              />
            </div>
          </div>

          {/* Monto pagado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto Pagado
            </label>
            <Input
              type="number"
              step="0.01"
              value={montoPagado}
              onChange={(e) => setMontoPagado(Number(e.target.value))}
            />
          </div>

          {/* Método de pago */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Método de Pago
            </label>
            <select
              value={metodoPago}
              onChange={(e) => setMetodoPago(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="efectivo"> Efectivo</option>
              <option value="transferencia"> Transferencia Bancaria</option>
              <option value="tarjeta"> Tarjeta de Crédito/Débito</option>
              <option value="mercado_pago"> Mercado Pago</option>
              <option value="cheque"> Cheque</option>
              <option value="otro"> Otro</option>
            </select>
          </div>

          {/* Archivos del pago */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Upload size={16} className="inline mr-1" />
              Comprobante de Pago (1 imagen + 1 PDF, máx. 10MB)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={handleFileSelectPago}
                className="hidden"
                id="file-upload-pago"
                disabled={totalFilesPago >= 2}
              />
              <label
                htmlFor="file-upload-pago"
                className={`px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer flex items-center gap-2 ${
                  totalFilesPago >= 2 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Upload size={18} className="text-gray-400" />
                <span className="text-sm text-gray-500">
                  {totalFilesPago >= 2 ? 'Límite alcanzado' : 'Seleccionar archivos'}
                </span>
              </label>
              <span className="text-xs text-gray-400">{totalFilesPago}/2 archivos</span>
              {uploadingPago && (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-blue-600" />
                  <span className="text-xs text-blue-600">
                    {uploadProgressPago > 0 ? `Subiendo... ${uploadProgressPago}%` : 'Subiendo...'}
                  </span>
                </div>
              )}
            </div>

            {/* Archivos seleccionados */}
            {selectedFilesPago.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Archivos seleccionados:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedFilesPago.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                      {file.type === 'application/pdf' ? (
                        <File size={14} className="text-red-500" />
                      ) : (
                        <Image size={14} className="text-blue-500" />
                      )}
                      <span className="text-xs text-gray-600 truncate max-w-25">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeSelectedFilePago(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Imagen subida */}
            {imagenPago && (
              <div className="mt-2">
                <div className="items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 inline-flex">
                  <Image size={16} className="text-blue-500" />
                  <a href={imagenPago} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-xs">
                    {imagenPago.split('/').pop() || 'Ver imagen'}
                  </a>
                  <button type="button" onClick={removeImagenPago} className="text-red-500 hover:text-red-700">
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* PDF subido */}
            {pdfPago && (
              <div className="mt-2">
                <div className="items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 inline-flex">
                  <File size={16} className="text-red-500" />
                  <a href={pdfPago} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-xs">
                    {pdfPago.split('/').pop() || 'Ver PDF'}
                  </a>
                  <button type="button" onClick={removePdfPago} className="text-red-500 hover:text-red-700">
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observaciones
            </label>
            <textarea
              value={observacionesPago}
              onChange={(e) => setObservacionesPago(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Observaciones del pago..."
            />
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handlePagarCuota}
              disabled={isSubmittingPago || uploadingPago}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <Wallet size={16} className="mr-2" />
              {isSubmittingPago || uploadingPago ? 'Procesando...' : 'Confirmar Pago'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setModalPago(false);
                setImagenPago(null);
                setPdfPago(null);
                setSelectedFilesPago([]);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================
      MODAL DE DETALLE DE PAGO
      ============================================ */}
      <Modal
        isOpen={modalDetallePago}
        onClose={() => {
          setModalDetallePago(false);
          setPagoSeleccionado(null);
        }}
        title={`Detalle del Pago #${pagoSeleccionado?.numeroCuota || ''}`}
        size="md"
      >
        {pagoSeleccionado && (
          <div className="space-y-4">
            {/* Información del pago */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Número de Cuota</p>
                <p className="font-semibold text-gray-800">#{pagoSeleccionado.numeroCuota}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Estado</p>
                {getEstadoPagoBadge(pagoSeleccionado.estado)}
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Monto Esperado</p>
                <p className="font-semibold text-gray-800">{formatCurrency(pagoSeleccionado.montoEsperado)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Monto Pagado</p>
                <p className="font-semibold text-green-600">{formatCurrency(pagoSeleccionado.montoPagado || 0)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Vencimiento</p>
                <p className="font-semibold text-gray-800">{formatDate(pagoSeleccionado.fechaVencimiento)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Fecha de Pago</p>
                <p className="font-semibold text-gray-800">
                  {pagoSeleccionado.fechaPago ? formatDate(pagoSeleccionado.fechaPago) : '-'}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg col-span-2">
                <p className="text-xs text-gray-500">Método de Pago</p>
                <p className="font-semibold text-gray-800 capitalize">{pagoSeleccionado.metodoPago || 'No especificado'}</p>
              </div>
            </div>

            {/* Observaciones */}
            {pagoSeleccionado.observaciones && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <MessageSquare size={14} />
                  Observaciones
                </p>
                <p className="text-sm text-gray-700 mt-1">{pagoSeleccionado.observaciones}</p>
              </div>
            )}

            {/* Archivos del pago */}
            {(pagoSeleccionado.imagenUrl || pagoSeleccionado.pdfUrl) && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Comprobantes</p>
                <div className="space-y-2">
                  {pagoSeleccionado.imagenUrl && (
                    <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                      <Image size={18} className="text-blue-500" />
                      <button
                        onClick={() => setPreviewImage(pagoSeleccionado.imagenUrl)}
                        className="text-blue-600 hover:underline text-sm flex-1 text-left"
                      >
                        Ver imagen del comprobante
                      </button>
                      <a
                        href={pagoSeleccionado.imagenUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Abrir
                      </a>
                    </div>
                  )}
                  {pagoSeleccionado.pdfUrl && (
                    <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                      <File size={18} className="text-red-500" />
                      <a
                        href={pagoSeleccionado.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm flex-1"
                      >
                        Ver PDF del comprobante
                      </a>
                      <a
                        href={pagoSeleccionado.pdfUrl}
                        download
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Descargar
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Botón cerrar */}
            <div className="flex justify-end pt-4 border-t">
              <Button
                type="button"
                onClick={() => {
                  setModalDetallePago(false);
                  setPagoSeleccionado(null);
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal para vista previa de imágenes */}
      <Modal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        title="Vista previa de imagen"
        size="lg"
      >
        <div className="flex items-center justify-center min-h-75">
          {previewImage && (
            <img
              src={previewImage}
              alt="Vista previa"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          )}
        </div>
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              if (previewImage) {
                window.open(previewImage, '_blank');
              }
            }}
            className="text-sm text-blue-600 hover:underline"
          >
            Abrir imagen en nueva pestaña
          </button>
        </div>
      </Modal>
    </div>
  );
}